"use client";

import { ChartLineIcon, CheckIcon, FileTextIcon } from "lucide-react";
import { CommandGroup, CommandItem } from "@/components/ui/command";
import type { AnalysisTarget } from "@/lib/ai/analysis-target";
import { cn } from "@/lib/utils";
import {
  AnalysisTargetProjectColorBadge,
  type AnalysisTargetTypeGroup,
  AnalysisTargetTypeIcon,
  getAnalysisTargetOptionKey,
} from "./analysis-target-options";

type Props = {
  groupedOptions: AnalysisTargetTypeGroup[];
  selectedTargets: AnalysisTarget[];
  onSelectTarget: (target: AnalysisTarget) => void;
  activeOptionKey?: string | null;
  onActiveOptionKeyChange?: (key: string) => void;
};

/**
 * ピッカーとメンションで共有する、IW/SA/UA 階層の候補描画ブロック。
 */
export function AnalysisTargetCommandGroups({
  groupedOptions,
  selectedTargets,
  onSelectTarget,
  activeOptionKey,
  onActiveOptionKeyChange,
}: Props) {
  const selectedKeys = new Set(
    selectedTargets.map((target) => getAnalysisTargetOptionKey(target)),
  );

  return groupedOptions.map((typeGroup) => (
    <CommandGroup
      key={typeGroup.projectType}
      className="!overflow-visible !p-0 [&_[cmdk-group-heading]]:sticky [&_[cmdk-group-heading]]:top-0 [&_[cmdk-group-heading]]:z-30 [&_[cmdk-group-heading]]:h-8 [&_[cmdk-group-heading]]:border-b [&_[cmdk-group-heading]]:bg-popover [&_[cmdk-group-heading]]:px-0 [&_[cmdk-group-heading]]:py-0"
      heading={
        <span className="inline-flex h-8 w-full items-center gap-1.5 px-2 text-[11px] uppercase tracking-wide">
          <AnalysisTargetTypeIcon
            projectType={typeGroup.projectType}
            className="size-3.5"
          />
          <span>{typeGroup.shortLabel}</span>
        </span>
      }
    >
      {typeGroup.projects.map((projectGroup) => {
        const projectSelected = selectedKeys.has(projectGroup.project.key);

        return (
          <div key={projectGroup.project.key} className="contents">
            <CommandItem
              data-analysis-target-key={projectGroup.project.key}
              value={projectGroup.project.key}
              disabled={projectSelected}
              onSelect={() => {
                if (projectSelected) {
                  return;
                }
                onSelectTarget(projectGroup.project.target);
              }}
              onMouseMove={() =>
                onActiveOptionKeyChange?.(projectGroup.project.key)
              }
              className={cn(
                "scroll-mt-10 data-[disabled=true]:opacity-100",
                projectSelected && "text-muted-foreground",
                activeOptionKey === projectGroup.project.key &&
                  "bg-accent text-accent-foreground",
              )}
            >
              <AnalysisTargetProjectColorBadge
                color={projectGroup.project.projectColor}
              />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate leading-tight font-medium">
                  {projectGroup.project.label}
                </span>
              </div>
              <CheckIcon
                className={cn(
                  "ml-auto size-4",
                  projectSelected ? "opacity-100" : "opacity-0",
                )}
              />
            </CommandItem>

            {projectGroup.views.length > 0 ? (
              <div className="ml-4 border-l border-border/70">
                {projectGroup.views.map((viewOption) => {
                  const viewSelected =
                    selectedKeys.has(viewOption.key) ||
                    selectedKeys.has(
                      getAnalysisTargetOptionKey(viewOption.target),
                    );
                  const detailIcon =
                    viewOption.group === "index-worker" ? (
                      <FileTextIcon className="size-3.5 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChartLineIcon className="size-3.5 shrink-0 text-muted-foreground" />
                    );
                  return (
                    <CommandItem
                      key={viewOption.key}
                      data-analysis-target-key={viewOption.key}
                      value={viewOption.key}
                      disabled={viewSelected}
                      onSelect={() => {
                        if (viewSelected) {
                          return;
                        }
                        onSelectTarget(viewOption.target);
                      }}
                      onMouseMove={() =>
                        onActiveOptionKeyChange?.(viewOption.key)
                      }
                      className={cn(
                        "scroll-mt-10 pl-3",
                        viewSelected && "opacity-70",
                        activeOptionKey === viewOption.key &&
                          "bg-accent text-accent-foreground",
                      )}
                    >
                      {detailIcon}
                      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className="truncate leading-tight">
                          {viewOption.label}
                        </span>
                      </div>
                      <CheckIcon
                        className={cn(
                          "ml-auto size-4",
                          viewSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </CommandGroup>
  ));
}
